import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import KpiCard from '../shared/KpiCard';
import constants from '../../../config';
import { CHART_COLORS } from '../shared/formatters';

class AnalyticsCards extends Component {
  render() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var data = this.props.data;
    if (!data) return <div className="tw-text-center tw-py-16 tw-text-[#999]">No data available.</div>;

    var topSelling = data.topSelling || [];
    var coldCards = data.coldCards || [];
    var setPerformance = data.setPerformance || [];
    var priceDistribution = data.priceDistribution || [];
    var rarityBreakdown = data.rarityBreakdown || [];
    var foilStats = data.foilStats || [];
    var dailySales = data.dailySales || [];

    // Format foil stats for display
    var foilData = foilStats.map(function(f) {
      return { name: f.foil ? 'Foil' : 'Regular', listings: f.listings, sold: f.sold, avgPrice: f.avgPrice };
    });

    return (
      <div>
        {/* KPI Cards */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
          <KpiCard icon="fa-check-circle" label="Sell-Through" value={data.sellThroughRate + '%'} color="#10B981" change="in period" />
          <KpiCard icon="fa-clock-o" label="Avg Days to Sell" value={data.avgDaysToSell + 'd'} color="#3B82F6" />
          <KpiCard icon="fa-fire" label="Top Selling" value={topSelling.length > 0 ? topSelling[0].cardName : '--'} color="#D4A843" change={topSelling.length > 0 ? topSelling[0].sales + ' sales' : ''} />
          <KpiCard icon="fa-snowflake-o" label="Cold Cards" value={coldCards.length} color="#6B7280" change="listed, no sales" />
          <KpiCard icon="fa-th-large" label="Sets Tracked" value={setPerformance.length} color="#8B5CF6" />
          <KpiCard icon="fa-diamond" label="Foil Premium" value={function() { var foil = foilData.find(function(f) { return f.name === 'Foil'; }); var reg = foilData.find(function(f) { return f.name === 'Regular'; }); if (!foil || !reg || !foil.avgPrice || !reg.avgPrice) return '--'; var diff = foil.avgPrice - reg.avgPrice; return (diff >= 0 ? '+' : '-') + '$' + Math.abs(diff).toFixed(2); }()} color="#F59E0B" />
        </div>

        {/* Daily Sales Volume */}
        <div className="tw-rounded-xl tw-p-5 tw-mb-6" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Daily Sales Volume</h3>
          {dailySales.length > 0 ? (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <AreaChart data={dailySales} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return v.slice(5); }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} tickFormatter={function(v) { return '$' + v; }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={function(v, name) { return [name === 'revenue' ? '$' + Number(v).toFixed(2) : v, name === 'revenue' ? 'Revenue' : 'Sales']; }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} fill="url(#salesGrad)" dot={false} activeDot={{ r: 5, fill: '#10B981' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No sales data yet</div>
          )}
        </div>

        {/* Row: Price Distribution + Rarity + Foil */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
          {/* Price Distribution */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Price Distribution</h3>
            {priceDistribution.length > 0 ? (
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <BarChart data={priceDistribution} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                    <XAxis dataKey="range" tick={{ fontSize: 9, fill: '#999' }} interval={0} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }} />
                    <Bar dataKey="count" name="Listings" fill="#D4A843" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data</div>
            )}
          </div>

          {/* Rarity Breakdown */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Rarity Breakdown</h3>
            {rarityBreakdown.length > 0 ? (
              <div>
                <div style={{ width: '100%', height: 160 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={rarityBreakdown} dataKey="sold" nameKey="rarity" cx="50%" cy="50%" outerRadius={60} innerRadius={35} paddingAngle={2}>
                        {rarityBreakdown.map(function(e, i) { return <Cell key={'r-' + i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                        itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff' }}
                        formatter={function(v, name) { return [v + ' sold']; }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="tw-space-y-1 tw-mt-2">
                  {rarityBreakdown.map(function(r, i) {
                    return (
                      <div key={'rl-' + i} className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                        <span className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-inline-block" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="tw-text-[#666] dark:tw-text-[#aaa]">{r.rarity}</span>
                        </span>
                        <span className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{r.listings} listed / {r.sold} sold</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data</div>
            )}
          </div>

          {/* Foil vs Regular */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Foil vs Regular</h3>
            {foilData.length > 0 ? (
              <div className="tw-space-y-3 tw-mt-4">
                {foilData.map(function(f, i) {
                  return (
                    <div key={'fl-' + i} className="tw-p-3 tw-rounded-lg" style={{ backgroundColor: i === 0 ? 'rgba(212,168,67,0.08)' : 'rgba(59,130,246,0.08)' }}>
                      <div className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">{f.name}</div>
                      <div className="tw-flex tw-justify-between tw-text-xs tw-text-[#666] dark:tw-text-[#aaa]">
                        <span>{f.listings} listed</span>
                        <span>{f.sold} sold</span>
                        <span className="tw-font-semibold" style={{ color: '#D4A843' }}>${f.avgPrice.toFixed(2)} avg</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No data</div>
            )}
          </div>
        </div>

        {/* Row: Top Selling + Cold Cards */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 tw-mb-6">
          {/* Top Selling Cards */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Top 15 Selling Cards</h3>
            {topSelling.length > 0 ? (
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead>
                    <tr className="tw-border-b tw-border-[#d0d0d0] dark:tw-border-[#555]">
                      <th className="tw-text-left tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Card</th>
                      <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Sales</th>
                      <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSelling.map(function(card, i) {
                      return (
                        <tr key={'ts-' + i} className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                          <td className="tw-py-2 tw-px-2">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              {card.image && <img src={card.image.startsWith('http') ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-7 tw-h-9 tw-object-contain tw-rounded" />}
                              <div>
                                <div className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-text-xs">{card.cardName}</div>
                                {card.cardNumber && <div className="tw-text-[10px] tw-text-[#999]">{card.cardNumber}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.sales}</td>
                          <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold" style={{ color: '#10B981' }}>${card.revenue.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No sales data</div>
            )}
          </div>

          {/* Cold Cards */}
          <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Cold Cards (Listed, No Sales)</h3>
            {coldCards.length > 0 ? (
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-sm">
                  <thead>
                    <tr className="tw-border-b tw-border-[#d0d0d0] dark:tw-border-[#555]">
                      <th className="tw-text-left tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Card</th>
                      <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Set</th>
                      <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Listings</th>
                      <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Lowest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coldCards.map(function(card, i) {
                      return (
                        <tr key={'cc-' + i} className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                          <td className="tw-py-2 tw-px-2">
                            <div className="tw-flex tw-items-center tw-gap-2">
                              {card.image && <img src={card.image.startsWith('http') ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-7 tw-h-9 tw-object-contain tw-rounded" />}
                              <div>
                                <div className="tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-text-xs">{card.name}</div>
                                {card.cardNumber && <div className="tw-text-[10px] tw-text-[#999]">{card.cardNumber}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="tw-text-right tw-py-2 tw-px-2 tw-text-[#666] dark:tw-text-[#aaa] tw-text-xs">{card.cardSet}</td>
                          <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{card.listingCount}</td>
                          <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold" style={{ color: '#EF4444' }}>{card.lowestPrice != null ? '$' + card.lowestPrice.toFixed(2) : '--'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="tw-text-center tw-py-8 tw-text-[#999]">No cold cards</div>
            )}
          </div>
        </div>

        {/* Set Performance */}
        <div className="tw-rounded-xl tw-p-5" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-4">Set Performance</h3>
          {setPerformance.length > 0 ? (
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-border-b tw-border-[#d0d0d0] dark:tw-border-[#555]">
                    <th className="tw-text-left tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Set</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Listings</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Sold</th>
                    <th className="tw-text-right tw-py-2 tw-px-2 tw-text-xs tw-font-medium tw-text-[#999] tw-uppercase">Avg Price</th>
                  </tr>
                </thead>
                <tbody>
                  {setPerformance.map(function(s, i) {
                    return (
                      <tr key={'sp-' + i} className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                        <td className="tw-py-2 tw-px-2 tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{s.set || 'Unknown'}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-text-[#666] dark:tw-text-[#aaa]">{s.listings}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">{s.sold}</td>
                        <td className="tw-text-right tw-py-2 tw-px-2 tw-font-semibold" style={{ color: '#D4A843' }}>${s.avgPrice.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tw-text-center tw-py-8 tw-text-[#999]">No set data</div>
          )}
        </div>
      </div>
    );
  }
}

export default AnalyticsCards;
